import {
    Box,
    Button,
    Checkbox,
    ContextualBanner,
    FooterBanner,
    Info,
    Modal,
    SearchSelect,
    StaticTable,
    Status,
    StatusBadge,
    TextBlock,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    Warning,
} from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";

import { GroupSelect } from "components/asset-management-system/custom-views/GroupSelect";
import style from "components/asset-management-system/upload/imported-assets-subpage.scss";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { AssetPathKey, MANDATORY_ASSET_FIELDS, resolveAssetPathKey } from "domain/asset-management/assetPathMapping";
import { MAX_IMPORT_FILE_SIZE_BYTES, MAX_IMPORT_FILE_SIZE_MEGABYTES } from "domain/asset-management/assets";
import { assetService } from "services/asset-management/AssetService";
import { createTranslatorPath } from "services/pathTranslator";
import { isStringBlank } from "utils/commonFunctions";

import testIds from "testIds.json";

const PREVIEW_ROW_LIMIT = 3;

interface ColumnMapping {
    enabled: boolean;
    attribute: string | undefined;
}

interface AssetAttribute {
    label: string;
    value: string;
}

interface Props {
    headers: string[];
    rows: string[][];
    file: File;
    groupUuid?: string;
    onDone: (withSuccess: boolean) => void;
}

export function ImportedAssetsSubpage(props: Props) {
    const { t } = useTranslation();
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");
    const [submitLoading, setSubmitLoading] = React.useState(false);
    const [existingKeys, setExistingKeys] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [isOverwriteModalOpen, setIsOverwriteModalOpen] = React.useState(false);
    const [selectedGroupUuid, setSelectedGroupUuid] = React.useState(props.groupUuid ?? "");

    const [columnMappings, setColumnMappings] = React.useState<ColumnMapping[]>(
        props.headers.map((header) => ({ enabled: true, attribute: resolveAssetPathKey(header) }))
    );
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [missingAttributes, setMissingAttributes] = React.useState<string[]>([]);
    const [isFileSizeExceeded, setIsFileSizeExceeded] = React.useState(false);

    React.useEffect(() => {
        if (loading) {
            fetchExistingKeys();
        }
    }, [loading]);

    const newColumnCount = columnMappings.filter(
        (mapping) => mapping.enabled && mapping.attribute === undefined
    ).length;

    const getColumnValues = (columnIndex: number): string[] => {
        return props.rows.slice(0, PREVIEW_ROW_LIMIT).map((row) => row[columnIndex]);
    };

    const handleCheckboxToggle = (columnIndex: number) => {
        setColumnMappings((previous) =>
            previous.map((mapping, index) =>
                index === columnIndex ? { ...mapping, enabled: !mapping.enabled } : mapping
            )
        );
    };

    const hasAnyColumnEnabled = (): boolean => columnMappings.some((mapping) => mapping.enabled);

    const hasAllColumnsEnabled = (): boolean => columnMappings.every((mapping) => mapping.enabled);

    const handleToggleAllColumns = (checked: boolean) => {
        setColumnMappings((previous) => previous.map((mapping) => ({ ...mapping, enabled: checked })));
    };

    const handleAttributeChange = (columnIndex: number, value: string) => {
        setColumnMappings((previous) =>
            previous.map((mapping, index) => (index === columnIndex ? { ...mapping, attribute: value } : mapping))
        );
        if (missingAttributes.includes(value)) {
            setMissingAttributes(missingAttributes.filter((each) => (each !== value ? each : null)));
        }
    };

    const buildMappedCsv = (): File => {
        const enabledIndices = columnMappings
            .map((mapping, index) => (mapping.enabled ? index : -1))
            .filter((index) => index !== -1);

        const mappedHeaders = enabledIndices.map((index) => {
            const mapping = columnMappings[index];
            return mapping.attribute ?? props.headers[index];
        });

        const escapeCsvField = (field: string): string => {
            if (field.includes(",") || field.includes('"') || field.includes("\n")) {
                return `"${field.replace(/"/g, '""')}"`;
            }
            return field;
        };

        const csvRows = [
            mappedHeaders.map(escapeCsvField).join(","),
            ...props.rows.map((row) => enabledIndices.map((index) => escapeCsvField(row[index])).join(",")),
        ];

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        return new File([blob], props.file.name, { type: "text/csv" });
    };

    const handleSubmit = () => {
        setIsFileSizeExceeded(false);
        const unmappedAttributes = MANDATORY_ASSET_FIELDS.filter((field) => {
            const columnIndex = columnMappings.findIndex((column) => column.attribute === field && column.enabled);
            if (columnIndex === -1) {
                return true;
            }

            return props.rows.some((row) => isStringBlank(row[columnIndex]));
        }).map((field) => translatedPaths(field));

        if (unmappedAttributes.length > 0) {
            setMissingAttributes(unmappedAttributes);
            return;
        }
        setIsOverwriteModalOpen(true);
    };

    const handleUpload = () => {
        const mappedFile = buildMappedCsv();
        if (mappedFile.size > MAX_IMPORT_FILE_SIZE_BYTES) {
            setIsFileSizeExceeded(true);
            return;
        }
        setSubmitLoading(true);
        assetService
            .uploadAssets(mappedFile, undefined, selectedGroupUuid || undefined)
            .then(() => {
                props.onDone(true);
            })
            .catch(() => {
                props.onDone(false);
            })
            .finally(() => {
                setSubmitLoading(false);
            });
    };

    const fetchExistingKeys = () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        assetService.fetchExistingKeys(abortController).then((response) => {
            setExistingKeys(response.keys);
            setLoading(false);
        });
    };

    const createMappingAttributes = (): AssetAttribute[] => {
        const allKnownKeys: string[] = Object.values(AssetPathKey);
        const existingAttributes: AssetAttribute[] = [];
        existingKeys.map((each) => {
            if (!allKnownKeys.includes(each)) {
                existingAttributes.push({ label: each, value: each });
            }
        });
        const knownAttributes: AssetAttribute[] = allKnownKeys.map((each) => ({
            label: translatedPaths(each),
            value: each,
        }));
        return knownAttributes.concat(existingAttributes).sort((a, b) => a.label.localeCompare(b.label));
    };

    const deduceMappedAttribute = (csvColumnName: string): string | undefined => {
        return resolveAssetPathKey(csvColumnName) ?? (existingKeys.includes(csvColumnName) ? csvColumnName : undefined);
    };

    React.useEffect(() => {
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, []);

    return (
        <>
            <SubpageLayout
                title={t("AssetManagement.upload.fileSummary.title")}
                visible={true}
                buttons={
                    <Button
                        variant={"Primary"}
                        onClick={handleSubmit}
                        testId={testIds.workArea.asset.upload.fileSummarySubpageView.saveButton}
                    >
                        {t("Common.import")}
                    </Button>
                }
                loading={submitLoading}
                footerBanner={
                    missingAttributes.length > 0 ? (
                        <FooterBanner
                            text={`
                            ${t("AssetManagement.upload.fileSummary.errorMessage")} ${"\n"}
                            ${t("AssetManagement.upload.fileSummary.unmappedAttributes", {
                                unmappedAttributes: missingAttributes.join(", "),
                            })}`}
                        />
                    ) : (
                        isFileSizeExceeded && (
                            <FooterBanner
                                text={t("AssetManagement.upload.fileUploadModal.error.fileSizeExceeded", {
                                    maxSizeMegabytes: MAX_IMPORT_FILE_SIZE_MEGABYTES,
                                })}
                            />
                        )
                    )
                }
            >
                {loading ? (
                    <LoadingIndicator />
                ) : (
                    <div className={style.mapColumnsSection}>
                        <TextBlock>
                            {t("AssetManagement.upload.fileSummary.mapColumnsDescription", {
                                count: props.rows.length,
                            })}
                        </TextBlock>
                        <Box columnLayout gap="Lg" paddingBottom="Md">
                            <GroupSelect
                                name="groupSelect"
                                value={selectedGroupUuid}
                                onChange={setSelectedGroupUuid}
                                disabled={!!props.groupUuid}
                            />
                        </Box>
                        <ContextualBanner variant="Warning" className={style.mapColumnsSection}>
                            <Box columnLayout gap="Xxxs">
                                <TextBlock>{t("AssetManagement.upload.fileSummary.overwriteWarning")}</TextBlock>
                            </Box>
                        </ContextualBanner>
                        <StaticTable
                            headers={[
                                {
                                    value: (
                                        <Checkbox
                                            checked={hasAllColumnsEnabled()}
                                            indeterminate={hasAnyColumnEnabled() && !hasAllColumnsEnabled()}
                                            hideLabelSection
                                            onChange={(event) => handleToggleAllColumns(event.target.checked)}
                                            name="columnCheckbox"
                                        />
                                    ),
                                },
                                { value: t("AssetManagement.upload.fileSummary.columnHeader") },
                                {
                                    value: (
                                        <Box gap="Xxs" verticalAlignment="Center">
                                            {t("AssetManagement.upload.fileSummary.previewInformation")}
                                            <Tooltip>
                                                <TooltipTrigger>
                                                    <Info />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {t("AssetManagement.upload.fileSummary.columnsDisplayed", {
                                                        count: Math.min(props.rows.length, PREVIEW_ROW_LIMIT),
                                                    })}
                                                </TooltipContent>
                                            </Tooltip>
                                        </Box>
                                    ),
                                },
                                { value: t("AssetManagement.upload.fileSummary.mappingStatus") },
                                { value: t("AssetManagement.upload.fileSummary.mapToAttribute") },
                            ]}
                            cells={props.headers.map((header, columnIndex) => {
                                const values = getColumnValues(columnIndex);
                                const mapping = columnMappings[columnIndex];
                                const effectiveAttribute = mapping.attribute ?? deduceMappedAttribute(header);
                                const isMapped = effectiveAttribute !== undefined;
                                const isDuplicate =
                                    isMapped &&
                                    mapping.enabled &&
                                    props.headers.some((otherHeader, otherIndex) => {
                                        if (otherIndex === columnIndex || !columnMappings[otherIndex].enabled) {
                                            return false;
                                        }
                                        const otherAttribute =
                                            columnMappings[otherIndex].attribute ?? deduceMappedAttribute(otherHeader);
                                        return otherAttribute === effectiveAttribute && columnIndex > otherIndex;
                                    });
                                return [
                                    <Checkbox
                                        key={`checkbox-${columnIndex}`}
                                        name={`column-${columnIndex}`}
                                        checked={mapping.enabled}
                                        onChange={() => handleCheckboxToggle(columnIndex)}
                                        hideLabelSection={true}
                                    />,
                                    header,
                                    <div key={`preview-${columnIndex}`} className={style.previewValues}>
                                        {values.map((value, rowIndex) => (
                                            <span key={rowIndex}>{value}</span>
                                        ))}
                                    </div>,
                                    isMapped ? (
                                        <StatusBadge
                                            value={t("AssetManagement.upload.fileSummary.mapped")}
                                            status={Status.SUCCESS}
                                        />
                                    ) : (
                                        <StatusBadge value={t("Common.new")} status={Status.INFO} />
                                    ),
                                    <SearchSelect
                                        key={`select-${columnIndex}`}
                                        name={`attribute-${columnIndex}`}
                                        placeholder={t("AssetManagement.upload.fileSummary.selectAttribute")}
                                        items={createMappingAttributes()}
                                        onSelection={(value: string) => handleAttributeChange(columnIndex, value)}
                                        hideLabelSection={true}
                                        showTooltips={true}
                                        selectedByDefault={deduceMappedAttribute(header)}
                                        dropdownMaxHeight={250}
                                        readOnly={false}
                                        error={
                                            isDuplicate
                                                ? t("AssetManagement.upload.fileSummary.duplicateAttribute")
                                                : undefined
                                        }
                                    />,
                                ];
                            })}
                        />
                    </div>
                )}
            </SubpageLayout>
            <Modal
                isOpen={isOverwriteModalOpen}
                hideModal={() => setIsOverwriteModalOpen(false)}
                title={
                    <Box gap="Xxs">
                        <Warning />
                        {t("AssetManagement.upload.overwriteWarningModal.title")}
                    </Box>
                }
                footerBanner={
                    <ContextualBanner variant="Warning" className={style.overwriteModalBanner}>
                        {t("AssetManagement.upload.overwriteWarningModal.footerBanner")}
                    </ContextualBanner>
                }
                buttons={
                    <Box paddingTop="Sm" gap="Sm">
                        <Button
                            variant="Primary"
                            onClick={() => {
                                setIsOverwriteModalOpen(false);
                                handleUpload();
                            }}
                        >
                            {t("AssetManagement.upload.overwriteWarningModal.continue")}
                        </Button>
                        <Button variant="Secondary" onClick={() => setIsOverwriteModalOpen(false)}>
                            {t("AssetManagement.upload.overwriteWarningModal.cancel")}
                        </Button>
                    </Box>
                }
            >
                <Box columnLayout gap="Xxs">
                    {t("AssetManagement.upload.overwriteWarningModal.info")}
                    <ul>
                        {/* TODO BCC-6338 Update the "new fileds" 'amount' information once the way to add new columns is determined */}
                        <li>
                            {t("AssetManagement.upload.overwriteWarningModal.assetsImported", {
                                amount: props.rows.length,
                            })}
                        </li>
                        <li>
                            {t("AssetManagement.upload.overwriteWarningModal.columnsImported", {
                                amount: columnMappings.filter((column) => column.enabled).length,
                            })}
                        </li>
                        <li>
                            {t("AssetManagement.upload.overwriteWarningModal.fieldsCreated", {
                                amount: newColumnCount,
                            })}
                        </li>
                    </ul>
                </Box>
            </Modal>
        </>
    );
}
